import { browserLogger } from "@@/settings";
import { useSessionStorage } from "@@/shared/use-session-storage";
import type { LanguageCode, ProviderId } from "@towni/common";
import {
    hashCode,
    languageCodeMap,
    languageCodes,
    languageCodeZodSchema,
    parseSafely,
} from "@towni/common";
import React, { createContext, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import type { StoreApi } from "zustand";
import { createStore } from "zustand";
import { shallow } from "zustand/shallow";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { useProviderLanguages } from "./use-provider-languages";

type ContextState = State & Actions;
type State = {
    readonly language: LanguageCode;
    readonly availableLanguages: LanguageCode[] | readonly LanguageCode[];
};

type Actions = {
    readonly setLanguage: (language: LanguageCode) => void;
    readonly setAvailableLanguages: (
        languages: LanguageCode[] | readonly LanguageCode[],
    ) => void;
};

const languageToEditContext = createContext<StoreApi<ContextState> | undefined>(
    undefined,
);
const searchParamKey = "lang-edit";

type Props = {
    readonly id: string;
    readonly initialLanguage?: LanguageCode;
    readonly initiallyAvailableLanguages?: LanguageCode[];
    readonly children?: React.ReactNode;
    readonly providerId?: ProviderId;
};

const LanguageToEditContextProvider = (props: Props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [providerLanguages, providerLanguagesPending] = useProviderLanguages(
        props.providerId,
    );
    const languageFromUrl = parseSafely({
        value: searchParams.get(searchParamKey),
        schema: languageCodeZodSchema,
    });
    const [langageFromSessionStorage, setLangageFromSessionStorage] =
        useSessionStorage<LanguageCode | undefined>(
            `language-edit_${hashCode(props.id)}`,
            undefined,
        );

    const store = useRef<StoreApi<ContextState>>(
        createStore<ContextState>()((set, _get) => {
            const setLanguage = (language: LanguageCode): void => {
                set(context => ({ ...context, language }));
            };
            const setAvailableLanguages = (
                availableLanguages: LanguageCode[],
            ): void => {
                set(context => ({ ...context, availableLanguages }));
            };

            const state: State = {
                language:
                    props.initialLanguage ??
                    languageFromUrl ??
                    langageFromSessionStorage ??
                    languageCodeMap.sv,
                availableLanguages: props.initiallyAvailableLanguages?.length
                    ? props.initiallyAvailableLanguages
                    : providerLanguages.defined &&
                        providerLanguages.languages.length
                      ? providerLanguages.languages
                      : languageCodes,
            };

            return {
                ...state,
                setLanguage,
                setAvailableLanguages,
            } as ContextState;
        }),
    );

    // When url changes, change value in store
    useEffect(() => {
        browserLogger.log(
            "LanguageToEditContextProvider",
            "useEffect languageFromUrl",
            {
                languageFromUrl,
            },
        );
        store.current.setState({
            language: languageFromUrl ?? store.current.getState().language,
        });
    }, [languageFromUrl]);

    // When provider languages are not pending,
    // set available languages if necessary
    useEffect(() => {
        browserLogger.log(
            "LanguageToEditContextProvider",
            "useEffect",
            "SET AVAILABLE LANGUAGES",
            {
                providerLanguages,
                providerLanguagesPending,
                initiallyAvailableLanguages: props.initiallyAvailableLanguages,
            },
        );
        if (!providerLanguagesPending) {
            store.current.setState({
                availableLanguages: props.initiallyAvailableLanguages?.length
                    ? props.initiallyAvailableLanguages
                    : providerLanguages.defined &&
                        providerLanguages.languages.length
                      ? providerLanguages.languages
                      : languageCodes,
            });
        }
    }, [
        props.initiallyAvailableLanguages,
        providerLanguages,
        providerLanguagesPending,
    ]);

    store.current.subscribe((state, prevState) => {
        if (state.language !== prevState.language) {
            // When language is changed, store it session storage for a bit
            // to keep it safe from monsters and other evil things
            const language = state.language;
            if (langageFromSessionStorage !== language)
                setLangageFromSessionStorage(language);
            if (language === "sv") searchParams.delete(searchParamKey);
            else {
                if (languageFromUrl !== language) {
                    setSearchParams(
                        searchParams => {
                            searchParams.set(searchParamKey, language);
                            return searchParams;
                        },
                        { replace: true },
                    );
                }
            }
        }
    });

    browserLogger.log(
        "LanguageToEditContextProvider",
        "state",
        store.current.getState(),
        {
            providerLanguages,
            providerLanguagesPending,
            langageFromSessionStorage,
            languageFromUrl,
            props,
        },
    );

    return (
        <languageToEditContext.Provider value={store.current}>
            {props.children}
        </languageToEditContext.Provider>
    );
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const useLanguageToEditContext = <U extends unknown = ContextState>(
    selector: (context: ContextState) => U = context => context as unknown as U,
): U => {
    const store = React.useContext(languageToEditContext);
    if (store === undefined) {
        throw new Error(
            "useLanguageToEditContext must be used within a LanguageToEditContextProvider",
        );
    }
    return useStoreWithEqualityFn(store, selector, shallow);
};

export { LanguageToEditContextProvider, useLanguageToEditContext };
